import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context";
import { Reveal } from "@/components/Reveal";

export default function Fees() {
  const { t, setService } = useLang();
  const navigate = useNavigate();

  const talk = () => {
    setService("ongoing");
    navigate("/contact");
  };

  const Panel = ({ plan, featured }) => (
    <div
      data-testid={featured ? "fees-executive" : "fees-essential"}
      className={`group relative h-full flex flex-col overflow-hidden border p-9 sm:p-12 lg:p-14 transition-colors duration-500 ${
        featured
          ? "bg-charcoal border-charcoal"
          : "bg-cream/50 border-charcoal/15 hover:border-gold/60"
      }`}
    >
      <span className="absolute top-0 left-0 h-px w-0 bg-gold transition-all duration-700 group-hover:w-full" />
      {featured && (
        <span
          data-testid="fees-popular-badge"
          className="absolute top-9 right-9 sm:top-12 sm:right-12 text-[10px] uppercase tracking-[0.28em] text-gold"
        >
          {plan.badge}
        </span>
      )}
      <p
        className={`text-[11px] uppercase tracking-[0.3em] ${featured ? "text-gold" : "text-taupe"}`}
      >
        {plan.name}
      </p>
      <div className="mt-8 flex items-baseline gap-3">
        <span
          className={`font-serif text-6xl sm:text-7xl font-light tracking-tight ${
            featured ? "text-ivory" : "text-charcoal"
          }`}
        >
          {plan.price}
        </span>
        <span className={`text-xs uppercase tracking-[0.2em] ${featured ? "text-taupe" : "text-taupe"}`}>
          {plan.per}
        </span>
      </div>
      <p className={`mt-4 text-xs uppercase tracking-[0.26em] ${featured ? "text-gold" : "text-gold"}`}>
        {plan.hours}
      </p>
      <p
        className={`mt-5 max-w-sm text-sm sm:text-base leading-relaxed font-light ${
          featured ? "text-taupe" : "text-ink/70"
        }`}
      >
        {plan.desc}
      </p>
      <div className="mt-9">
        {t.fees.list.map((item) => (
          <div
            key={item}
            className={`border-t py-3.5 text-sm font-light ${
              featured ? "border-ivory/10 text-ivory/80" : "border-taupe/25 text-ink/75"
            }`}
          >
            {item}
          </div>
        ))}
        <div className={featured ? "border-t border-ivory/10" : "border-t border-taupe/25"} />
      </div>
      <button
        data-testid={featured ? "fees-executive-cta" : "fees-essential-cta"}
        onClick={talk}
        className={`mt-auto pt-10 flex items-center gap-3 text-[11px] uppercase tracking-[0.26em] transition-colors duration-300 w-fit ${
          featured ? "text-ivory hover:text-gold" : "text-charcoal hover:text-gold"
        }`}
      >
        {t.contact.cta} <ArrowRight size={14} className="text-gold" />
      </button>
    </div>
  );

  return (
    <section data-testid="fees-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Reveal className="flex items-center gap-4 mb-10 sm:mb-14">
          <span className="gold-rule w-14" aria-hidden="true" />
          <span className="text-[11px] sm:text-xs uppercase tracking-[0.34em] text-gold">{t.fees.label}</span>
        </Reveal>

        <div className="grid grid-cols-12 gap-y-8 lg:gap-16 items-end mb-16 sm:mb-20">
          <Reveal className="col-span-12 lg:col-span-7 min-w-0">
            <h2
              data-testid="fees-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal font-medium leading-[1.08]"
            >
              {t.fees.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 min-w-0">
            <p data-testid="fees-intro" className="text-sm sm:text-base text-ink/70 leading-relaxed font-light lg:pb-2">
              {t.fees.intro}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5">
          <Reveal className="col-span-12 lg:col-span-6">
            <Panel plan={t.fees.essential} featured={false} />
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 lg:col-span-6">
            <Panel plan={t.fees.executive} featured={true} />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <p data-testid="fees-hours-note" className="mt-6 text-[11px] sm:text-xs tracking-[0.06em] text-taupe font-light leading-relaxed">
            {t.fees.hoursNote}
          </p>
          <p data-testid="fees-tax-note" className="mt-2 text-[11px] sm:text-xs tracking-[0.06em] text-taupe font-light leading-relaxed">
            {t.fees.taxNote}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="fees-tailored"
            className="mt-14 sm:mt-16 border border-taupe/25 px-9 py-10 sm:px-12 grid grid-cols-12 gap-y-8 lg:gap-10 items-center"
          >
            <div className="col-span-12 lg:col-span-7 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{t.fees.tailored.label}</p>
              <p className="mt-4 font-serif text-2xl sm:text-3xl text-charcoal font-medium leading-snug">
                {t.fees.tailored.p1}
              </p>
              <p className="mt-3 max-w-lg text-sm sm:text-base text-ink/65 leading-relaxed font-light">
                {t.fees.tailored.p2}
              </p>
            </div>
            <div className="col-span-12 lg:col-span-5 min-w-0 lg:text-right">
              <p data-testid="fees-tailored-price" className="font-serif text-3xl sm:text-4xl text-charcoal font-light">
                {t.fees.tailored.price}
              </p>
              <button
                data-testid="fees-tailored-cta"
                onClick={talk}
                className="mt-5 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.26em] text-charcoal hover:text-gold transition-colors duration-300"
              >
                {t.contact.cta} <ArrowRight size={14} className="text-gold" />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-24 sm:mt-28 border-t border-taupe/25 pt-14">
          <Reveal>
            <h3 data-testid="fees-outside-title" className="font-serif text-2xl sm:text-3xl text-charcoal font-medium">
              {t.fees.outside.title}
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-4 max-w-xl text-sm sm:text-base text-ink/65 leading-relaxed font-light">
              {t.fees.outside.text}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p
              data-testid="fees-outside-line"
              className="mt-10 text-[11px] sm:text-xs uppercase tracking-[0.24em] text-taupe leading-loose"
            >
              {t.fees.outside.line}
            </p>
          </Reveal>
        </div>

        <div className="mt-24 sm:mt-28 text-center max-w-2xl mx-auto">
          <Reveal>
            <h3 data-testid="fees-bespoke-title" className="font-serif text-3xl sm:text-4xl text-charcoal font-medium">
              {t.fees.bespoke.title}
            </h3>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-sm sm:text-base text-ink/65 leading-relaxed font-light">
              {t.fees.bespoke.text}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <button
              data-testid="fees-bespoke-cta"
              onClick={talk}
              className="mt-10 inline-flex items-center gap-3 border border-charcoal px-9 py-4 text-[11px] uppercase tracking-[0.24em] text-charcoal hover:border-gold hover:text-gold transition-colors duration-300"
            >
              {t.contact.cta} <ArrowRight size={14} className="text-gold" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
